import React, { useState, useEffect } from "react";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRangePicker } from "react-date-range";
import { Popover } from "antd";
import { BiCalendar } from "react-icons/bi";
import { format } from "date-fns";

interface DateRangeProps {
  value?: Date | null | string;
  onChange: (date: string) => void;
  placeholder: string;
}

const DateRange = ({ value, onChange, placeholder }: DateRangeProps) => {
  // Helper function to check if value is valid
  const isValidDate = (val: any): val is Date => {
    return val instanceof Date && !isNaN(val.getTime());
  };

  // Helper function to convert value to Date or undefined
  const getInitialDate = (val: any): Date | undefined => {
    if (!val) return undefined;
    if (val instanceof Date) return isValidDate(val) ? val : undefined;
    if (typeof val === "string") {
      const date = new Date(val);
      return isValidDate(date) ? date : undefined;
    }
    return undefined;
  };

  const initialDate = getInitialDate(value);

  const [selectionRange, setSelectionRange] = useState({
    startDate: initialDate,
    endDate: initialDate,
    key: "selection",
  });

  // Sync internal state with value prop
  useEffect(() => {
    const newDate = getInitialDate(value);
    setSelectionRange({
      startDate: newDate,
      endDate: newDate,
      key: "selection",
    });
  }, [value]);

  const handleSelect = (ranges: any) => {
    setSelectionRange(ranges.selection);
    if (ranges.selection.startDate && ranges.selection.endDate) {
      const dateRangeString = `${format(
        ranges.selection.startDate,
        "dd/MM/yyyy"
      )} - ${format(ranges.selection.endDate, "dd/MM/yyyy")}`;
      onChange(dateRangeString);
    }
  };

  return (
    <Popover
      content={
        <DateRangePicker
          ranges={[selectionRange]}
          onChange={handleSelect}
          months={1}
          direction="horizontal"
          showDateDisplay={false}
        />
      }
      trigger="click"
      placement="bottom"
    >
      <button className="border border-[#D0D5DD] rounded-lg p-2 flex justify-start items-center gap-2">
        <BiCalendar strokeWidth={0.2} size={20} color="#9CA3AF" />
        {selectionRange.startDate && selectionRange.endDate ? (
          <p className="text-sm font-medium">
            {format(selectionRange.startDate, "dd/MM/yyyy")} -{" "}
            {format(selectionRange.endDate, "dd/MM/yyyy")}
          </p>
        ) : (
          <p className="text-sm font-medium">
            {placeholder || "Select date range"}
          </p>
        )}
      </button>
    </Popover>
  );
};

export default DateRange;
